// src/components/common/AppNavbar.tsx

import { useState } from "react";
import { Button, Dropdown, Navbar } from "react-daisyui";
import {
  FaBuilding,
  FaCarSide,
  FaMotorcycle,
  FaStore,
  FaTruck,
  FaUsers,
  FaUserTie,
} from "react-icons/fa";
import { HiMenuAlt2 } from "react-icons/hi";
import { HiArrowNarrowRight } from "react-icons/hi";
import { Link, useNavigate } from "react-router-dom"; // Use react-router-dom
import { twMerge } from "tailwind-merge";

import Language from "@/components/common/Language";
import Megamenu from "@/components/common/MegaMenu";
import Theme from "@/components/common/Theme";
import "@/styles/custom.css";

export default function AppNavbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const navigate = useNavigate();

  // Helper to route to auth login with role parameter
  const goToDashboard = (role: string) => {
    navigate(`/auth/login?role=${role}`);
    setLoginOpen(false);
  };

  return (
    <>
      <Navbar
        className={twMerge(
          "fixed z-40 bg-base-100/40 backdrop-blur-md",
          "border-b border-b-base-200/50",
        )}
      >
        <Navbar.Start>
          <Button
            aria-label="Open menu"
            color="ghost"
            onClick={() => setMenuOpen(true)}
            shape="circle"
          >
            <HiMenuAlt2 className="h-5 w-5" />
          </Button>

          <Link
            className="ml-2 text-2xl font-bold transition-all sm:text-2xl md:text-3xl lg:text-4xl"
            to="/"
          >
            {import.meta.env.VITE_APP_NAME || "Hareeng"}
          </Link>
        </Navbar.Start>

        <Navbar.End>
          <Language className="mr-1 hidden sm:inline-flex" />
          <Theme className="mr-1" />

          <Dropdown
            className="pr-3"
            end
            onBlur={() => setLoginOpen(false)}
            onClick={() => setLoginOpen(!loginOpen)}
            open={loginOpen}
          >
            <Dropdown.Toggle button={false}>
              <Button
                aria-label="Login"
                className="rounded-full bg-transparent px-6 font-semibold text-primary shadow-none transition-all hover:scale-105 hover:bg-base-200"
                color="ghost"
              >
                <span className="flex items-center gap-2">
                  <HiArrowNarrowRight className="h-5 w-5" />
                  Login
                </span>
              </Button>
            </Dropdown.Toggle>

            <Dropdown.Menu className="menu-compact mt-3 w-56 rounded-xl shadow-xl">
              <Dropdown.Item
                className="flex items-center justify-between gap-2"
                onClick={() => goToDashboard("driver")}
              >
                <span className="flex items-center gap-2">
                  <FaCarSide className="text-primary" />
                  Driver
                </span>
                <HiArrowNarrowRight className="animate-bounce-x" />
              </Dropdown.Item>
              <Dropdown.Item
                className="flex items-center justify-between gap-2"
                onClick={() => goToDashboard("delivery")}
              >
                <span className="flex items-center gap-2">
                  <FaTruck className="text-primary" />
                  Delivery Driver
                </span>
                <HiArrowNarrowRight className="animate-bounce-x" />
              </Dropdown.Item>
              <Dropdown.Item
                className="flex items-center justify-between gap-2"
                onClick={() => goToDashboard("scooter")}
              >
                <span className="flex items-center gap-2">
                  <FaMotorcycle className="text-primary" />
                  Scooter Driver
                </span>
                <HiArrowNarrowRight className="animate-bounce-x" />
              </Dropdown.Item>
              <Dropdown.Item
                className="flex items-center justify-between gap-2"
                onClick={() => goToDashboard("restaurant")}
              >
                <span className="flex items-center gap-2">
                  <FaStore className="text-primary" />
                  Restaurant Owner
                </span>
                <HiArrowNarrowRight className="animate-bounce-x" />
              </Dropdown.Item>
              <Dropdown.Item
                className="flex items-center justify-between gap-2"
                onClick={() => goToDashboard("fleet")}
              >
                <span className="flex items-center gap-2">
                  <FaUsers className="text-primary" />
                  Fleet Owner
                </span>
                <HiArrowNarrowRight className="animate-bounce-x" />
              </Dropdown.Item>
              <Dropdown.Item
                className="flex items-center justify-between gap-2"
                onClick={() => goToDashboard("business")}
              >
                <span className="flex items-center gap-2">
                  <FaBuilding className="text-primary" />
                  Business Owner
                </span>
                <HiArrowNarrowRight className="animate-bounce-x" />
              </Dropdown.Item>
              <Dropdown.Item
                className="flex items-center justify-between gap-2"
                onClick={() => goToDashboard("courier")}
              >
                <span className="flex items-center gap-2">
                  <FaUserTie className="text-primary" />
                  Courier Owner
                </span>
                <HiArrowNarrowRight className="animate-bounce-x" />
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </Navbar.End>
      </Navbar>
      <Megamenu onClose={() => setMenuOpen(false)} open={menuOpen} />
    </>
  );
}
